import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  AdminPanelSettings as AdminIcon,
  Business as PartnerIcon,
  Dashboard as DashboardIcon,
  AccountBalance as AccountingIcon,
} from '@mui/icons-material';
import { useAuth } from './AuthProvider';

interface RoleToggleProps {
  size?: 'small' | 'medium';
}

export const RoleToggle: React.FC<RoleToggleProps> = ({ size = 'small' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isInsurancePartner, canAccessPortal } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const showAdminPortal = canAccessPortal('admin');
  const showAccountingPortal = canAccessPortal('accounting');
  const showPartnerPortal = isSuperAdmin || canAccessPortal('insurance');

  // Only show if user has access to more than one portal area
  if (!showAdminPortal && !showAccountingPortal && !showPartnerPortal) {
    return null;
  }

  const isInAdminArea = location.pathname.startsWith('/admin');
  const isInPartnerArea = location.pathname.startsWith('/partner');
  const isInAccountingArea = location.pathname.startsWith('/accounting');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const getCurrentView = () => {
    if (isInAdminArea) return 'Admin';
    if (isInPartnerArea) return 'Partner';
    if (isInAccountingArea) return 'Accounting';
    return 'Main';
  };

  return (
    <>
      <Tooltip title="Switch view">
        <Button
          size={size}
          variant="outlined"
          onClick={handleClick}
          startIcon={<SwapIcon />}
          sx={{
            textTransform: 'none',
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
            },
          }}
        >
          {getCurrentView()}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 2,
          sx: { minWidth: 200, mt: 1 },
        }}
      >
        <MenuItem onClick={() => handleNavigate('/')} selected={!isInAdminArea && !isInPartnerArea && !isInAccountingArea}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Main Dashboard" />
        </MenuItem>
        <Divider />
        {showAdminPortal && (
          <MenuItem onClick={() => handleNavigate('/admin')} selected={isInAdminArea}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Admin Portal" />
          </MenuItem>
        )}
        {showAccountingPortal && (
          <MenuItem onClick={() => handleNavigate('/accounting')} selected={isInAccountingArea}>
            <ListItemIcon>
              <AccountingIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Accounting Portal" secondary="DealerTrack reports" />
          </MenuItem>
        )}
        {showPartnerPortal && (
          <MenuItem onClick={() => handleNavigate('/partner')} selected={isInPartnerArea}>
            <ListItemIcon>
              <PartnerIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Partner Portal"
              secondary={isInsurancePartner ? 'You have partner access' : 'Preview mode'}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default RoleToggle;
